import { CartLine, ProductVariant, Product } from '@shopify/hydrogen-react/storefront-api-types';
import { extractUrlLastPart } from './storefront/utils';
import { ElevarCustomerData } from '../store/analytics/types/AnalyticsData';

declare global {
  interface Window {
    ElevarDataLayer: any[];
    ElevarInvalidateContext: () => void;
  }
}

export interface CustomerDetails {
  defaultAddress?: {
    address1?: string;
    address2?: string;
    city?: string;
    country?: string;
    province?: string;
    provinceCode?: string;
    zip?: string;
  };
  email?: string;
  firstName?: string;
  id?: string;
  lastName?: string;
  numberOfOrders?: number;
  phone?: string;
  tags?: string[];
}

const mapProductsToImpressions = (
  rawData: { productVariant: ProductVariant; product: Product }[]
) => {
  const impressions = rawData.map((item, index) => {
    return mapProductToImpression(item, index);
  });

  return impressions;
};

const mapProductToImpression = (
  item: { productVariant: ProductVariant; product: Product },
  index: number
) => {
  const Impression = {
    id: item.productVariant.sku,
    name: item.product.title,
    brand: item.product.vendor,
    category:
      item.product.collections == undefined
        ? ''
        : item.product.collections.edges.map(a => a.node.title).toString(),
    variant: item.productVariant.title,
    price: item.productVariant.price.amount,
    list: location.href,
    product_id: extractUrlLastPart(item.product.id),
    variant_id: extractUrlLastPart(item.productVariant.id),
    compare_at_price: item.productVariant.compareAtPrice?.amount || '0.0',
    position: index + 1,
  };

  return Impression;
};

const mapCartLines = (
  rawData: CartLine[],
  isProduct = true,
  showPosition = false,
  productUrl = ''
) => {
  const cartLines = rawData.map((item, index) => {
    return mapCartLinesItem(item, isProduct, showPosition, productUrl, index);
  });

  return cartLines;
};

const mapCartLinesItem = (
  data: CartLine,
  isProduct = true,
  showPosition = false,
  productUrl = '',
  index?: number
) => {
  const mappedData = {
    id: data.merchandise.sku,
    name: data.merchandise.product.title,
    brand: data.merchandise.product.vendor,
    category:
      data.merchandise.product.collections == undefined
        ? ''
        : data.merchandise.product.collections.edges.map(a => a.node.title).toString(),
    variant: data.merchandise.title,
    price: data.merchandise.price.amount,
    quantity: data.quantity.toString(),
    list: location.href,
    product_id: extractUrlLastPart(data.merchandise.product.id),
    variant_id: extractUrlLastPart(data.merchandise.id),
    compare_at_price: data.merchandise.compareAtPrice?.amount || '0.0',
  };

  if (isProduct) {
    mappedData['image'] = data.merchandise.image?.url
      ? data.merchandise.image.url
      : data.merchandise.product.featuredImage?.url || '';
    mappedData['url'] = window.location.href;
  }

  if (showPosition) {
    mappedData['position'] = typeof index === 'number' ? index + 1 : 0;
  }

  if (productUrl) {
    mappedData['url'] = productUrl;
  }

  return mappedData;
};

export const loadElevarUserData = (
  customer: ElevarCustomerData | null,
  lines: CartLine[],
  cartTotal?: string
) => {
  pushData({
    event: 'dl_user_data',
    cart_total: cartTotal ? cartTotal : '0.00',
    user_properties: customer,
    ecommerce: {
      currencyCode: 'USD',
      cart_contents: {
        products: mapCartLines(lines),
      },
    },
  });
};

export const elevarAddToCart = (
  customer: ElevarCustomerData | null,
  productData: Product,
  productVariant: ProductVariant,
  quantity: number,
  productUrl?: string,
  listPage?: string | undefined
) => {
  pushData({
    event: 'dl_add_to_cart',
    user_properties: customer,
    ecommerce: {
      currencyCode: 'USD',
      add: {
        actionField: {
          list: listPage ? listPage : '',
        },
        products: [
          {
            id: productVariant.sku, // SKU
            name: productData.title,
            brand: productData.vendor,
            category:
              productData.collections == undefined
                ? ''
                : productData.collections.edges.map(a => a.node.title).toString(),
            variant: productVariant.title,
            price: productVariant.price.amount,
            quantity: quantity.toString(),
            list: listPage ? listPage : '',
            product_id: extractUrlLastPart(productData.id),
            variant_id: extractUrlLastPart(productVariant.id),
            compare_at_price: productVariant.compareAtPrice?.amount ?? '0.0',
            image: productVariant.image?.url
              ? productVariant.image.url
              : productData.featuredImage?.url || '',
            url: productUrl ? productUrl : window.location.href,
          },
        ],
      },
    },
  });
};

export const elevarAddToCartFromCart = (
  customer: ElevarCustomerData | null,
  linesItem: CartLine,
  productUrl?: string
) => {
  linesItem.quantity = 1;
  pushData({
    event: 'dl_add_to_cart',
    user_properties: customer,
    ecommerce: {
      currencyCode: 'USD',
      add: {
        actionField: {
          list: '',
        },
        products: [mapCartLinesItem(linesItem, true, false, productUrl)],
      },
    },
  });
};

export const elevarRemoveFromCart = (
  customer: ElevarCustomerData | null,
  linesItem: CartLine,
  quantity: number
) => {
  linesItem.quantity = quantity;
  pushData({
    event: 'dl_remove_from_cart',
    user_properties: customer,
    ecommerce: {
      currencyCode: 'USD',
      remove: {
        actionField: {
          list: '',
        },
        products: [mapCartLinesItem(linesItem)],
      },
    },
  });
};

export const elevarSelectItem = (
  customer: ElevarCustomerData | null,
  productData: Product,
  productVariant: ProductVariant,
  position?: number
) => {
  pushData({
    event: 'dl_select_item',
    user_properties: customer,
    ecommerce: {
      currencyCode: 'USD',
      click: {
        actionField: {
          list: location.pathname,
          action: 'click',
        },
        products: [
          {
            id: productVariant.sku, // SKU
            name: productData.title,
            brand: productData.vendor,
            category:
              productData.collections == undefined
                ? ''
                : productData.collections.edges.map(a => a.node.title).toString(),
            variant: productVariant.title,
            price: productVariant.price.amount,
            list: location.href,
            product_id: extractUrlLastPart(productData.id),
            variant_id: extractUrlLastPart(productVariant.id),
            compare_at_price: productVariant.compareAtPrice?.amount ?? '0.0',
            image: productVariant.image?.url
              ? productVariant.image.url
              : productData.featuredImage?.url || '',
            url: window.location.href,
            position: position === 0 ? position + 1 : position,
          },
        ],
      },
    },
  });
};

export const elevarViewItem = (
  customer: ElevarCustomerData | null,
  productData: Product,
  productVariant: ProductVariant,
  listPage?: string
) => {
  pushData({
    event: 'dl_view_item',
    user_properties: customer,
    ecommerce: {
      currencyCode: 'USD',
      detail: {
        actionField: {
          list: listPage ? listPage : '',
          action: 'detail',
        },
        products: [
          {
            id: productVariant.sku,
            name: productData.title,
            brand: productData.vendor,
            category:
              productData.collections == undefined
                ? ''
                : productData.collections.edges.map(a => a.node.title).toString(),
            variant: productVariant.title,
            price: productVariant.price.amount,
            list: listPage ? listPage : '',
            product_id: extractUrlLastPart(productData.id),
            variant_id: extractUrlLastPart(productVariant.id),
            compare_at_price: productVariant.compareAtPrice?.amount ?? '0.0',
            image: productVariant.image?.url
              ? productVariant.image.url
              : productData.featuredImage?.url || '',
            url: window.location.href,
          },
        ],
      },
    },
  });
};

export const elevarViewCart = (
  customer: ElevarCustomerData | null,
  lines: CartLine[],
  cartTotal?: string
) => {
  pushData({
    event: 'dl_view_cart',
    user_properties: customer,
    cart_total: cartTotal ? cartTotal : '0.00',
    ecommerce: {
      currencyCode: 'USD',
      actionField: {
        list: 'Shopping Cart',
      },
      impressions: mapCartLines(lines, false, true),
    },
  });
};

export const elevarSubscribe = (email: string) => {
  pushData({
    event: 'dl_subscribe',
    lead_type: 'email',
    user_properties: {
      customer_email: email,
    },
  });
};

export const elevarViewProductsList = (
  customer: ElevarCustomerData | null,
  products: { productVariant: ProductVariant; product: Product }[]
) => {
  pushData({
    event: 'dl_view_item_list',
    user_properties: customer,
    ecommerce: {
      currencyCode: 'USD',
      impressions: mapProductsToImpressions(products),
    },
  });
};

export const elevarSearchImpressions = (
  customer: ElevarCustomerData | null,
  products: { productVariant: ProductVariant; product: Product }[]
) => {
  pushData({
    event: 'dl_view_search_results',
    user_properties: customer,
    ecommerce: {
      currencyCode: 'USD',
      actionField: {
        list: 'search results',
      },
      impressions: mapProductsToImpressions(products),
    },
  });
};

const pushData = (data: any) => {
  if (process.env.NODE_ENV === 'development') {
    return;
  }

  let ElevarDataLayer = window.ElevarDataLayer || [];
  ElevarDataLayer.push(data);
};
